import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Define types
export interface Module {
  id: string;
  title: string;
  topics: string[];
  duration: string;
}

export interface Assignment {
  id: string;
  title: string;
  deadline: string;
  status: 'pending' | 'completed';
}

export interface Course {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  instructor: string;
  duration: string;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  enrolled: number;
  price: number;
  rating: number;
  totalRatings: number;
  enrollmentStatus: 'available' | 'enrolled' | 'completed';
  modules: Module[];
  assignments: Assignment[];
}

interface CourseState {
  courses: Course[];
  enrolledCourses: Course[];
  currentCourse: Course | null;
  filteredCourses: Course[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  selectedCategory: string;
}

// Initial state
const initialState: CourseState = {
  courses: [],
  enrolledCourses: [],
  currentCourse: null,
  filteredCourses: [],
  isLoading: false,
  error: null,
  searchQuery: '',
  selectedCategory: 'All',
};

// Mock data - replace with actual API calls later
const mockCourses: Course[] = [
  {
    id: '1',
    title: 'MERN Stack Development',
    description: 'Learn to build complete web applications with MongoDB, Express, React, and Node.js.',
    thumbnail: '/lovable-uploads/376116c6-dcc8-4470-84a5-081b1528a7c6.png',
    instructor: 'Ajay Prammasani',
    duration: '3 Months',
    category: 'Application Development',
    level: 'Advanced',
    enrolled: 30,
    price: 25000,
    rating: 4,
    totalRatings: 280,
    enrollmentStatus: 'available',
    modules: [
      {
        id: 'm1',
        title: 'Introduction to MERN',
        topics: ['MongoDB Basics', 'Express Fundamentals', 'React Core Concepts', 'Node.js Essentials'],
        duration: '2 weeks'
      },
      {
        id: 'm2',
        title: 'Backend Development',
        topics: ['REST API Design', 'MongoDB Atlas', 'Express Routing', 'Authentication & Authorization'],
        duration: '3 weeks'
      }
    ],
    assignments: [
      { id: 'a1', title: 'Build a REST API', deadline: '2023-05-15', status: 'completed' },
      { id: 'a2', title: 'Create a React Frontend', deadline: '2023-06-01', status: 'pending' }
    ]
  },
  {
    id: '2',
    title: 'React Web Development',
    description: 'Master React.js and build modern, responsive web applications with the most popular JavaScript library.',
    thumbnail: '/lovable-uploads/376116c6-dcc8-4470-84a5-081b1528a7c6.png',
    instructor: 'Ajay Prammasani',
    duration: '3 Months',
    category: 'Web Development',
    level: 'Advanced',
    enrolled: 120,
    price: 25000,
    rating: 5,
    totalRatings: 280,
    enrollmentStatus: 'available',
    modules: [
      {
        id: 'm1',
        title: 'React Fundamentals',
        topics: ['JSX Syntax', 'Components', 'Props vs State', 'Event Handling'],
        duration: '2 weeks'
      }
    ],
    assignments: [
      { id: 'a1', title: 'Build a Todo App', deadline: '2023-05-10', status: 'completed' }
    ]
  }
];

// Mock API calls
const mockFetchCoursesApi = async (): Promise<Course[]> => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Return mock data
  return mockCourses;
};

const mockFetchCourseApi = async (id: string): Promise<Course> => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Find course by ID
  const course = mockCourses.find(course => course.id === id);
  
  if (!course) {
    throw new Error('Course not found');
  }
  
  return course;
};

const mockEnrollCourseApi = async (id: string): Promise<Course> => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Find course by ID
  const course = mockCourses.find(course => course.id === id);
  
  if (!course) {
    throw new Error('Course not found');
  }
  
  // Return enrolled course
  return {
    ...course,
    enrollmentStatus: 'enrolled'
  };
};

// Create async thunks
export const fetchCourses = createAsyncThunk(
  'course/fetchCourses',
  async (_, { rejectWithValue }) => {
    try {
      return await mockFetchCoursesApi();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch courses');
    }
  }
);

export const fetchCourse = createAsyncThunk(
  'course/fetchCourse',
  async (id: string, { rejectWithValue }) => {
    try {
      return await mockFetchCourseApi(id);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch course');
    }
  }
);

export const enrollCourse = createAsyncThunk(
  'course/enrollCourse',
  async (id: string, { rejectWithValue }) => {
    try {
      return await mockEnrollCourseApi(id);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to enroll in course');
    }
  }
);

// Helper function to filter courses
const filterCourses = (courses: Course[], searchQuery: string, category: string): Course[] => {
  return courses.filter(course => {
    const matchesSearch = 
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = category === 'All' || course.category === category;
    
    return matchesSearch && matchesCategory;
  });
};

// Create slice
const courseSlice = createSlice({
  name: 'course',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.filteredCourses = filterCourses(state.courses, action.payload, state.selectedCategory);
    },
    setSelectedCategory: (state, action: PayloadAction<string>) => {
      state.selectedCategory = action.payload;
      state.filteredCourses = filterCourses(state.courses, state.searchQuery, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch courses
      .addCase(fetchCourses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.courses = action.payload;
        state.filteredCourses = action.payload;
        state.error = null;
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch course
      .addCase(fetchCourse.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCourse.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCourse = action.payload;
        state.error = null;
      })
      .addCase(fetchCourse.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Enroll course
      .addCase(enrollCourse.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(enrollCourse.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // Update the enrollmentStatus in the courses array
        state.courses = state.courses.map(course => 
          course.id === action.payload.id 
            ? { ...course, enrollmentStatus: 'enrolled' } 
            : course
        );
        
        // Update filtered courses
        state.filteredCourses = state.filteredCourses.map(course => 
          course.id === action.payload.id 
            ? { ...course, enrollmentStatus: 'enrolled' } 
            : course
        );
        
        // Add to enrolled courses
        state.enrolledCourses = [...state.enrolledCourses, action.payload];
        
        state.error = null;
      })
      .addCase(enrollCourse.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { setSearchQuery, setSelectedCategory } = courseSlice.actions;

// Export reducer
export default courseSlice.reducer;